import { getSelectedCountryCode } from "@utils/countryUtils";
import { authenticatedFetch } from "@utils/authenticatedFetch";
import { requireApiOrigin } from "@utils/apiOrigin";
import { getCurrentUserId } from "@utils/userUtils";
import {
  reportingPeriodToApiParams,
  type NetworkReportingPeriod,
} from "@utils/networkReportingPeriod";
import type { NetworkDashboardMeta } from "@services/networkDashboard";

export type NetworkFacilityDetailTab =
  | "header"
  | "risk-profile"
  | "survey"
  | "staffing"
  | "quality"
  | "penalties-vbp"
  | "ownership";

export type NetworkFacilityDetailRequestOptions = {
  reportingPeriod?: NetworkReportingPeriod;
  signal?: AbortSignal;
};

export interface NetworkFacilityPagerItem {
  ccn: string;
  provider_name?: string | null;
  risk_score?: number | null;
}

export interface NetworkFacilityHeader {
  ccn: string;
  provider_name?: string | null;
  city?: string | null;
  state?: string | null;
  provider_address?: string | null;
  beds?: number | null;
  ownership_type?: string | null;
  chain_name?: string | null;
  risk_score?: number | null;
  risk_band?: { label: string; tone: string };
  network_rank?: { rank: number; total: number; highest_risk?: boolean };
  pager?: { prev: NetworkFacilityPagerItem | null; next: NetworkFacilityPagerItem | null };
  as_of_date?: string | null;
}

export interface NetworkFacilityRiskFactor {
  key: string;
  label: string;
  weight: number;
  index: number;
  points: number;
  max_points: number;
  tone: string;
}

export interface NetworkFacilityFlagCard {
  key: string;
  label: string;
  status: string;
  tone: string;
  value?: string | null;
  detail?: string | null;
  citations?: { citation_code?: string; severity?: string; event_date?: string }[];
}

export interface NetworkFacilityRiskProfile {
  header: NetworkFacilityHeader;
  risk_hero: {
    score?: number | null;
    max_score: number;
    band?: { label: string; tone: string };
    network_rank?: NetworkFacilityHeader["network_rank"];
    factors: NetworkFacilityRiskFactor[];
    chain_comparison: {
      key: string;
      label: string;
      value?: number | null;
      rank?: number | null;
      total?: number | null;
      display?: string | null;
      tone?: string;
    }[];
    computed_at?: string | null;
  };
  flag_cards: NetworkFacilityFlagCard[];
  timeline?: {
    event_date?: string | null;
    kind?: string | null;
    title?: string | null;
    detail?: string | null;
    tone?: string | null;
  }[];
  top_citations?: Record<string, unknown>[];
  citation_summary?: Record<string, unknown>;
  penalties?: Record<string, unknown>;
  ownership_summary?: Record<string, unknown>[];
}

export interface NetworkFacilityDetailMeta extends NetworkDashboardMeta {
  ccn?: string;
  linked_facility_id?: number | null;
  linked_facility_name?: string | null;
  link_hint?: string | null;
}

export interface NetworkFacilityHeaderTabData {
  header: NetworkFacilityHeader;
}

export interface NetworkFacilityDetailResponse<T = Record<string, unknown>> {
  data: T;
  meta: NetworkFacilityDetailMeta;
}

function mergeReportingPeriodParams(
  extra: Record<string, string | number | undefined> | undefined,
  options?: NetworkFacilityDetailRequestOptions,
): Record<string, string | number | undefined> | undefined {
  if (!options?.reportingPeriod) return extra;
  const periodParams = reportingPeriodToApiParams(options.reportingPeriod);
  return { ...extra, ...periodParams };
}

export async function getNetworkFacilityDetail<T = Record<string, unknown>>(
  ccn: string,
  root: { facilityId?: number; networkId?: number },
  tab: NetworkFacilityDetailTab = "risk-profile",
  options?: NetworkFacilityDetailRequestOptions,
): Promise<NetworkFacilityDetailResponse<T>> {
  const mergedExtra = mergeReportingPeriodParams({ tab }, options);
  const userId = getCurrentUserId();
  const country = getSelectedCountryCode() || "USA";
  const params = new URLSearchParams({
    user_id: String(userId),
    country_code: country,
  });
  if (root.networkId != null) {
    params.set("network_id", String(root.networkId));
  } else if (root.facilityId != null) {
    params.set("facility_id", String(root.facilityId));
  }
  if (mergedExtra) {
    Object.entries(mergedExtra).forEach(([k, v]) => {
      if (v !== undefined && v !== null) {
        params.set(k, String(v));
      }
    });
  }
  const url = `${requireApiOrigin()}/api/v1/network-dashboard/facilities/${encodeURIComponent(ccn)}?${params.toString()}`;
  const res = await authenticatedFetch(url, { signal: options?.signal });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg =
      (typeof err?.detail === "string" && err.detail) ||
      (typeof err?.message === "string" && err.message) ||
      `Network facility detail request failed (${res.status})`;
    throw new Error(msg);
  }
  const json = await res.json();
  return {
    data: (json.data ?? json.result?.data ?? {}) as T,
    meta: (json.meta ?? json.result?.meta ?? {}) as NetworkFacilityDetailMeta,
  };
}

